/* eslint-disable react/button-has-type */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';

import { usePrevious } from '../../hooks';

const errorMessagePropTypes = {
    // eslint-disable-next-line react/forbid-prop-types
    errorMessages: PropTypes.array.isRequired,
};

const ErrorMessage = ({ errorMessages }) => {
    if (!errorMessages) return null;

    const items = errorMessages
        // eslint-disable-next-line react/no-array-index-key
        .map((item, idx) => <p key={idx}>{item}</p>);

    return <div className="freelancer-details-section__errors">{items}</div>;
};

ErrorMessage.propTypes = errorMessagePropTypes;

const propTypes = {
    // eslint-disable-next-line react/forbid-prop-types
    freelancerData: PropTypes.object.isRequired,
    editionEnabled: PropTypes.bool.isRequired,
    // eslint-disable-next-line react/forbid-prop-types
    travelDistanceOptions: PropTypes.array.isRequired,
    onSave: PropTypes.func.isRequired,
    isAgent: PropTypes.bool.isRequired,
};

export default function FreelancerDetailsContact({
    freelancerData,
    travelDistanceOptions,
    editionEnabled,
    isAgent,
    onSave,
}) {
    const [isEdit, setIsEdit] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessages, setErrorMessages] = useState(null);
    const [address1, setAddress1] = useState(freelancerData.address1);
    const [address2, setAddress2] = useState(freelancerData.address2);
    const [postcode, setPostcode] = useState(freelancerData.postcode);
    const [city, setCity] = useState(freelancerData.city);
    const [travelDistance, setTravelDistance] = useState(
        freelancerData.travel_distance,
    );

    const prevIsEdit = usePrevious(isEdit);

    useEffect(() => {
        const initFields = () => {
            setAddress1(freelancerData.address1);
            setAddress2(freelancerData.address2);
            setPostcode(freelancerData.postcode);
            setCity(freelancerData.city);
            setTravelDistance(freelancerData.travel_distance);
        };

        if (prevIsEdit !== isEdit) initFields();
    }, [
        freelancerData.address1,
        freelancerData.address2,
        freelancerData.postcode,
        freelancerData.city,
        freelancerData.travel_distance,
        prevIsEdit,
        isEdit,
    ]);

    const setMessageOnError = error => {
        let messages = [];
        const { detail } = error.response.data.meta;

        if (typeof detail === 'object') {
            Object.keys(detail).forEach(key => {
                messages = [...messages, ...detail[key]];
            });
            setErrorMessages(messages);
        }

        setIsLoading(false);
    };

    const handleCancel = () => {
        setIsEdit(false);
        setErrorMessages(null);
    };

    const handleSave = () => {
        const fields = {
            address1,
            address2,
            postcode,
            city,
            travel_distance: travelDistance,
        };

        setIsLoading(true);
        setErrorMessages(null);

        onSave(fields)
            .then(() => {
                setIsEdit(false);
                setIsLoading(false);
            })
            .catch(error => {
                setMessageOnError(error);
            });
    };

    const option = travelDistanceOptions.find(
        item => item.value === freelancerData.travel_distance,
    );

    const label = option?.label ?? '';

    return (
        <div className="freelancer-details-section">
            <div className="freelancer-details-section__header">
                <div className="freelancer-details-section__title">
                    Location
                </div>
                {editionEnabled && isEdit ? (
                    <div className="freelancer-details-section__btns">
                        <button
                            disabled={isLoading}
                            className="freelancer-details-section__btn"
                            onClick={handleSave}
                        >
                            Save
                        </button>
                        <button
                            disabled={isLoading}
                            className="freelancer-details-section__btn"
                            onClick={handleCancel}
                        >
                            Cancel
                        </button>
                    </div>
                ) : (
                    isAgent && (
                        <div className="freelancer-details-section__btns">
                            <button
                                className="freelancer-details-section__btn"
                                onClick={() => setIsEdit(true)}
                            >
                                <i className="icon icon-pencil-3" />
                                Edit Info
                            </button>
                        </div>
                    )
                )}
            </div>
            <div>
                <ErrorMessage errorMessages={errorMessages} />
            </div>
            <div className="freelancer-details-section__content">
                <table className="table-default">
                    {isAgent ? (
                        <tbody>
                            <tr>
                                <td className="table-default__cell--text-blue">
                                    Address
                                </td>
                                {isEdit ? (
                                    <td>
                                        <input
                                            type="text"
                                            value={address1}
                                            onChange={e =>
                                                setAddress1(e.target.value)
                                            }
                                        />
                                        <input
                                            type="text"
                                            value={address2}
                                            onChange={e =>
                                                setAddress2(e.target.value)
                                            }
                                        />
                                    </td>
                                ) : (
                                    <td>
                                        <div>{freelancerData.address1}</div>
                                        <div>{freelancerData.address2}</div>
                                    </td>
                                )}
                            </tr>
                            <tr>
                                <td className="table-default__cell--text-blue">
                                    Postcode
                                </td>
                                {isEdit ? (
                                    <td>
                                        <input
                                            type="text"
                                            value={postcode}
                                            onChange={e =>
                                                setPostcode(e.target.value)
                                            }
                                        />
                                    </td>
                                ) : (
                                    <td>{freelancerData.postcode}</td>
                                )}
                            </tr>
                            <tr>
                                <td className="table-default__cell--text-blue">
                                    City
                                </td>
                                {isEdit ? (
                                    <td>
                                        <input
                                            type="text"
                                            value={city}
                                            onChange={e =>
                                                setCity(e.target.value)
                                            }
                                        />
                                    </td>
                                ) : (
                                    <td>{freelancerData.city}</td>
                                )}
                            </tr>
                            <tr>
                                <td className="table-default__cell--text-blue">
                                    Travel Distance
                                </td>
                                {isEdit ? (
                                    <td>
                                        <Select
                                            name="form-field-name"
                                            value={travelDistance}
                                            clearable={false}
                                            onChange={e =>
                                                setTravelDistance(e.value)
                                            }
                                            options={travelDistanceOptions}
                                        />
                                    </td>
                                ) : (
                                    <td>{label}</td>
                                )}
                            </tr>
                        </tbody>
                    ) : (
                        <tbody>
                            <tr>
                                <td className="table-default__cell--text-blue">
                                    Postcode
                                </td>
                                <td>{freelancerData.postcode}</td>
                            </tr>
                        </tbody>
                    )}
                </table>
            </div>
        </div>
    );
}

FreelancerDetailsContact.propTypes = propTypes;
